/**
 * The questionnaire export (plan-030 DOM-Q; design §41.5 `GET …/questionnaire-document/file`; FR-SQE-13).
 *
 * Two files of one version's questionnaire, one language at a time (a survey version has one language in practice,
 * design §41.0 F-2):
 *
 * - **JSON** — the stored file exactly, as `config_document.extraction.survey[<language>]` holds it, indented. A
 *   `wetalk-survey-json-v2` file exported this way and uploaded back through Edit is used verbatim, without the
 *   extraction model (AC-6, design §41.7). A v1 file is exported as it is stored too (an upload of it is read by the
 *   model, as today); the editor's first save moves it to v2 (§41.6.6).
 * - **Markdown** — the same questionnaire as text a person reads: each group's questions in order with their ordinal,
 *   code, type, wording, options and limits, then the replies, the closing and the other approved lines. Structure
 *   words are English; every questionnaire line is quoted as written, escaped so Markdown never reads it as markup.
 *
 * The api audits each export (`voice_agent.questionnaire_exported`); nothing here reads a clock or a network. Pure.
 */
import { SURVEY_OBJECTION, surveyArmOf, surveyDocumentOf, surveyVariantsOf, } from "./questionnaire.js";
import { numberedIndexOf, ordinalOf } from "./derive.js";
/** The two export formats (`file_format` of the route). */
export const QUESTIONNAIRE_FILE_FORMAT = ["json", "md"];
/** The media type each format is served as. */
export const QUESTIONNAIRE_FILE_CONTENT_TYPE = Object.freeze({
    json: "application/json; charset=utf-8",
    md: "text/markdown; charset=utf-8",
});
/** The stored file, exactly, as JSON text: two-space indentation and a final newline. */
export function questionnaireJsonOf(file) {
    return `${JSON.stringify(file, null, 2)}\n`;
}
/**
 * The attachment's file name, `<voice-agent-slug>-v<n>-questions.<json|md>` (design §41.5). The slug is reduced to
 * lower-case letters, digits and single hyphens, so the name is safe in a `Content-Disposition` header; a slug with
 * nothing left is refused rather than replaced.
 */
export function questionnaireFileNameOf(voiceAgentSlug, agentVersionSeq, fileFormat) {
    if (!Number.isInteger(agentVersionSeq) || agentVersionSeq < 1)
        throw new RangeError("An agent_version's number is a positive integer.");
    const slug = voiceAgentSlug
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/gu, "-")
        .replace(/^-+|-+$/gu, "");
    if (slug === "")
        throw new RangeError("The VoiceAgent's slug has no letter or digit to name the file by.");
    return `${slug}-v${String(agentVersionSeq)}-questions.${fileFormat}`;
}
/** A questionnaire line as inline Markdown text: the characters Markdown reads as inline markup escaped, lines kept. */
function textOf(line) {
    return line.replace(/[\\`*_[\]<>|~]/gu, "\\$&").replace(/\r?\n/gu, "  \n");
}
/** A line that starts a Markdown line: also never read as a heading, a list item or a quote. */
function blockOf(line) {
    return textOf(line).replace(/^(\s*)([#+>-]|\d+\.)/u, "$1\\$2");
}
function quoted(line) {
    return line === "" ? "*(silent)*" : textOf(line);
}
function questionLines(question, ordinal) {
    const lines = [];
    lines.push(`**${question.id}** · ${question.type}${question.code === question.id ? "" : ` · instrument code ${textOf(question.code)}`}`);
    if (ordinal !== null)
        lines.push(`Ordinal: ${quoted(ordinal)}`);
    else
        lines.push("Not numbered (asked without an ordinal).");
    lines.push(`Question: ${quoted(question.text)}`);
    switch (question.type) {
        case "scale":
            lines.push(`Scale words: ${quoted(question.suffix)}`);
            if (question.note !== undefined && question.note !== "")
                lines.push(`Note: ${quoted(question.note)}`);
            break;
        case "choice": {
            for (const option of question.options)
                lines.push(`- \`${option.id}\` ${quoted(option.label)}`);
            const selection = question.selection;
            if (selection !== undefined) {
                const picks = selection.min === selection.max ? String(selection.min) : `${String(selection.min)} to ${String(selection.max)}`;
                lines.push(`Picks: ${picks}`);
                if (selection.exclusive.length > 0)
                    lines.push(`Not combined with another pick: ${selection.exclusive.map((id) => `\`${id}\``).join(", ")}`);
                if (selection.more !== undefined)
                    lines.push(`Follow-up: ${quoted(selection.more)}`);
            }
            lines.push(`Clarification: ${quoted(question.clarify)}`);
            break;
        }
        case "open":
            lines.push(`Clarification: ${quoted(question.clarify)}`);
            break;
        case "number": {
            const range = question.min === null && question.max === null
                ? "any number"
                : `${question.min === null ? "any" : String(question.min)} to ${question.max === null ? "any" : String(question.max)}`;
            lines.push(`Range: ${range}${question.integer ? ", whole numbers" : ""}${question.unit === null ? "" : `, in ${textOf(question.unit)}`}`);
            lines.push(`Clarification: ${quoted(question.clarify)}`);
            break;
        }
    }
    return lines;
}
/** The questionnaire as a document a person reads. Throws on a file that is not a valid questionnaire (a stored one is). */
export function questionnaireMarkdownOf(file) {
    const read = surveyDocumentOf(file, "questionnaire");
    if (!read.ok)
        throw new Error(`The stored questionnaire is not valid: ${read.problem}`);
    const survey = read.survey;
    const out = [];
    out.push(`# ${textOf(survey.title)}`, "");
    out.push(`- Language: ${survey.language}`, `- Format: ${survey.format}`, `- Interviewer: ${textOf(survey.interviewer)}`, "");
    out.push("## Opening", "");
    out.push(`Greeting (morning): ${quoted(survey.greetings.morning)}  `);
    out.push(`Greeting (afternoon): ${quoted(survey.greetings.afternoon)}  `);
    if (survey.greetings.evening !== undefined)
        out.push(`Greeting (evening): ${quoted(survey.greetings.evening)}  `);
    out.push(`Introduction: ${quoted(survey.opening.intro)}  `);
    out.push(`When they decline: ${quoted(survey.opening.declined)}`, "");
    out.push("## Explanation", "");
    out.push(`${blockOf(survey.scale.intro)}  `);
    out.push(`Scale words: ${quoted(survey.scale.suffix)}`, "");
    const variants = surveyVariantsOf(survey);
    for (const [groupIndex, variant] of variants.entries()) {
        out.push(variants.length === 1 ? "## Questions" : `## Group ${String(groupIndex + 1)}`, "");
        const arm = surveyArmOf(survey, variant);
        for (const [index, question] of arm.questions.entries()) {
            const ordinal = question.numbered ? ordinalOf(survey, numberedIndexOf(arm, index), variant) : null;
            const [head, ...rest] = questionLines(question, ordinal);
            out.push(`${String(index + 1)}. ${head ?? ""}`);
            for (const line of rest)
                out.push(line.startsWith("- ") ? `    ${line}` : `    ${line}  `);
            out.push("");
        }
    }
    out.push("## Replies", "");
    for (const type of SURVEY_OBJECTION) {
        const reply = survey.objections[type];
        if (reply === undefined)
            continue;
        out.push(`- **${type}** — asked as ${quoted(reply.asks)}; reply: ${quoted(reply.answer)}`);
    }
    out.push("", "## Closing", "", survey.closing === "" ? quoted(survey.closing) : blockOf(survey.closing), "");
    out.push("## Other approved lines", "");
    for (const [name, line] of Object.entries(survey.helpers))
        out.push(`- ${name.replaceAll("_", " ")}: ${quoted(line)}`);
    out.push(`- de-escalation: ${quoted(survey.deescalation)}`);
    out.push(`- acknowledgements: ${file.acks.map(quoted).join(" · ")}`, "");
    return `${out.join("\n").trimEnd()}\n`;
}
//# sourceMappingURL=questionnaire-export.js.map